import React, { useState } from 'react';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent'
import DialogContentText from '@mui/material/DialogContentText';
import DialogActions from '@mui/material/DialogActions';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormControl from '@mui/material/FormControl';
import Box from '@mui/material/Box';

import reservationsAdapter, { IReservation } from '../adapters/reservationsAdapter';
import { IEmployee } from '../adapters/employeesAdapter';
import { IPropertyMgmtOrg } from '../adapters/propertyMgmtOrgsAdapter';
import useTranslator from '../../../utilities/hooks/useTranslator';
import useLocaleFormater from '../../../utilities/hooks/useLocaleFormater';
import { parseApiDate } from '../../../utilities/apiDates';
import LoadingState from '../../../components/LoadingState';
import { isApiConcurrencyError } from '../../../utilities/IApiConcurrencyError';
import { isApiBusinessLogicErrorWithCode } from '../../../utilities/IApiBusinessLogicError';
import useApiClient from '../../../utilities/hooks/useApiClient';

export interface IRestartDialogProps {
    openInfo: { open: boolean, reservation: IReservation } | undefined,
    setOpenInfo: (openInfo: { open: boolean, reservation: IReservation } | undefined) => void,
    allEmployees: IEmployee[],
    propertyMgmtOrg: IPropertyMgmtOrg,
    safeSetViewLoadingState: (loadingState: LoadingState) => void,
    refreshSingleReservationInList: (reservation: IReservation) => void,
    safeSetUserMessageContent: (messageContent: string) => void
}

const restartDialogDict = {
    restartDlgTitle: { en: 'Start Over', fr: 'Recommencer' },
    restartDlgTextBeforeJobName: { en: 'Start over employee on job ', fr: 'Recommencer pour l\'employé sur la job ' },
    restartDlgTextAfterJobName: { en: ' starting on {0} and that must be done before {1}.', fr: ' commençant le {0} et qui doit être terminée avant le {1}.' },
    restartDlgOkActionLbl: { en: 'Ok', fr: 'Ok' },
    restartDlgCancelActionLbl: { en: 'Cancel', fr: 'Annuler' },
    restartConcurrencyError: { en: 'Reservation for {0} has been modified by another user. Job cannot be started over for employee. Please refresh view and retry if needed.', fr: 'La réservation pour {0} a été modifiée par un autre utilisateurs. La job ne peut être recommencée pour l\'employé. SVP rafraîchir et réessayer si nécessaire.' },
    restartEmployeeAlreadyWorkingError: { en: 'Job cannot be started over for employee {0}. He is already working on another job.', fr: 'La job ne peut être recommencée pour l\'employé {0}. Il est déjà en train de travailler sur une autre job.' },
    restartInternalError: { en: 'Internal error while trying to start over job of {0} for {1}. Please refresh and retry if needed.', fr: 'Erreur interne lors du recommencement de la job pour {0} pour {1}. SVP rafraîchir et réessayer si nécessaire.' }
};

const RestartDialog = (props: IRestartDialogProps) => {

    const translate = useTranslator();
    const formater = useLocaleFormater();
    const apiClient = useApiClient();

    const [dialogEmployeeIdToRestart, setDialogEmployeeIdToRestart] = useState<string>('');

    const cancelRestartDialog = () => {

        setDialogEmployeeIdToRestart('');
        props.setOpenInfo({ open: false, reservation: props.openInfo!.reservation });
    }

    const getFormattedJobDate = (jobDate: string) => formater.formatDate(parseApiDate(jobDate), { month: 'long', day: 'numeric', hour: 'numeric', minute: 'numeric' });

    const getEmployeesToRestart = (reservation: IReservation) => reservation.cleaningJob.employees.filter((employee) => employee.status === 'Done');

    const getEmployee = (id: string) => props.allEmployees?.find((employee) => employee.id === id);

    const getFormattedEmployeeName = (employee: IEmployee) => employee.lastName + ' ' + employee.firstName;

    const getFormattedEmployeeNameWithId = (id: string) => {

        const employee = getEmployee(id);
        return employee ? getFormattedEmployeeName(employee) : '';
    };

    const restartEmployee = () => {

        const employeeToRestart = getEmployee(dialogEmployeeIdToRestart);

        if (!employeeToRestart)
            throw new Error('Internal error : Employee cannot be found.');

        const reservationToRestart = props.openInfo!.reservation;

        setDialogEmployeeIdToRestart('');
        props.setOpenInfo({ open: false, reservation: props.openInfo!.reservation });

        props.safeSetViewLoadingState(LoadingState.ModalWithVisibleContent);

        reservationsAdapter.restartEmployeeOnCleaningJob(
            apiClient,
            props.propertyMgmtOrg?.id,
            reservationToRestart.id,
            reservationToRestart.aggregateTimeStamp,
            { targetEmployeeUniqueUserId: employeeToRestart.uniqueUserId },
            true,
            ['EmployeeAlreadyWorksOnAnotherJob']
        )
            .then((value) => {
                props.refreshSingleReservationInList(value)
            })
            .catch((error: any) => {

                if (isApiConcurrencyError(error))
                    props.safeSetUserMessageContent(translate(restartDialogDict.restartConcurrencyError, [reservationToRestart.rentalUnit.name]));
                else if (isApiBusinessLogicErrorWithCode(error, 'EmployeeAlreadyWorksOnAnotherJob'))
                    props.safeSetUserMessageContent(translate(restartDialogDict.restartEmployeeAlreadyWorkingError, [getFormattedEmployeeName(employeeToRestart)]));
                else
                    props.safeSetUserMessageContent(translate(restartDialogDict.restartInternalError, [reservationToRestart.rentalUnit.name, getFormattedEmployeeName(employeeToRestart)]));
            })
            .finally(() => props.safeSetViewLoadingState(LoadingState.None));
    };

    return <Dialog fullWidth onClose={() => { }} open={!!props.openInfo && props.openInfo.open} keepMounted data-cy='RestartDialog'>
        <DialogTitle>{translate(restartDialogDict.restartDlgTitle)}</DialogTitle>
        <DialogContent dividers>
            <DialogContentText data-cy='RestartDialogText'>{translate(restartDialogDict.restartDlgTextBeforeJobName)}<Box component='span' sx={{ fontWeight: 700 }}>{props.openInfo?.reservation.rentalUnit.name}</Box>{translate(restartDialogDict.restartDlgTextAfterJobName, [props.openInfo ? getFormattedJobDate(props.openInfo.reservation.endDate) : '', props.openInfo ? getFormattedJobDate(props.openInfo.reservation.cleaningJob.mustBeCompletedBeforeDate) : ''])}</DialogContentText>
            <FormControl sx={{ mt: 2 }}>
                <RadioGroup
                    aria-labelledby="employees-to-start"
                    name="employees-to-start-rd"
                    onChange={(ev, value) => setDialogEmployeeIdToRestart(value)}
                    value={dialogEmployeeIdToRestart}>
                    {props.openInfo && getEmployeesToRestart(props.openInfo.reservation).map((cleaningEmployee, index) => <FormControlLabel value={cleaningEmployee.id} key={cleaningEmployee.id} control={<Radio />} label={getFormattedEmployeeNameWithId(cleaningEmployee.id)} data-cy={'RestartDialogEmployee_' + index.toString()} />)}
                </RadioGroup>
            </FormControl>
        </DialogContent>
        <DialogActions>
            <Button autoFocus onClick={cancelRestartDialog} data-cy='RestartDialogCancelAction'>{translate(restartDialogDict.restartDlgCancelActionLbl)}</Button>
            <Button disabled={dialogEmployeeIdToRestart === ''} onClick={restartEmployee} data-cy='RestartDialogOkAction'>{translate(restartDialogDict.restartDlgOkActionLbl)}</Button>
        </DialogActions>
    </Dialog>;
};

export default RestartDialog